import { from as rxFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { cancelBookingAddOns, getAvailableDates, getBookingAddOns, getTimeRanges } from '@hqo/resource-booking-client';
import {
  GetResourceAvailableDatesAPIResponse,
  GetResourceAvailableDatesParams,
} from 'store/resource-available-dates/types';
import { GetResourceTimeRangesAPIResponse, GetResourceTimeRangesParams } from 'store/resource-time-ranges/types';
import {
  BookingConversationsUnreadCountsMap,
  GetBookingConversationsUnreadCountsResponse,
} from 'store/booking-conversations/types';
import { GetBookingMessagesConversationResponse } from 'store/booking-messages-conversation/types';
import {
  FloorplanResourceAvailabilityResponse,
  FloorplanResponse,
  GetFloorplanResourcesAvailabilityResponse,
  ListFloorplansResponse,
} from 'store/api-client/floorplan-types';
import {
  CoworkerAttendanceEnvelopeResponse,
  CoworkerAttendanceParams,
  CoworkerAttendanceResponse,
  CoworkerGroupEnvelopeResponse,
  CoworkerGroupResponse,
  CoworkerResponse,
  CreateCoworkerGroupBody,
  ListCoworkerGroupsResponse,
  SearchCoworkersParams,
  SearchCoworkersResponse,
  UpdateCoworkerGroupBody,
} from 'store/api-client/coworker-types';
import { BookingLimitDay, BookingLimitDaysResponse } from 'store/api-client/booking-limit-types';
import { AddOnCancelOutcome, addOnCancelFailure, addOnCancelled } from 'store/api-client/add-on-cancel-types';
import { BookingAddOnsRead } from 'store/api-client/add-on-payment-types';

// resource-booking-service mounts everything behind the `resource-booking` global prefix
// (EXPOSED_ROUTES_PREFIX), and BookingConversationsUnreadController is `@Controller('resource-bookings/conversations')`.
const UNREAD_COUNTS_PATH = '/resource-booking/resource-bookings/conversations/unread-counts';

// ConversationsController (also resource-booking-service, same global prefix) is
// `@Controller('buildings/:buildingUuid/resource-bookings/:bookingId/conversations')` with a
// `@Get('messages')` handler — see booking-conversations.service.ts / conversations.controller.ts
// in the resource-booking-service repo.
const getBookingMessagesConversationPath = (buildingUuid: string, bookingId: number): string =>
  `/resource-booking/buildings/${buildingUuid}/resource-bookings/${bookingId}/conversations/messages`;

// FloorplanResourcesController (resource-booking-service, same global prefix) is
// `@Controller('buildings/:buildingUuid/floorplans/:floorplanUuid/resources')` with a
// `@Get('availability')` handler. Deliberately consumer-facing: it is gated on READ_BOOKINGS
// rather than the admin RESOURCE_BOOKING_CAN_MANAGE_FLOORPLAN every other floorplan route uses.
const getFloorplanResourcesAvailabilityPath = (buildingUuid: string, floorplanUuid: string): string =>
  `/resource-booking/buildings/${buildingUuid}/floorplans/${floorplanUuid}/resources/availability`;

// FloorplanController, `@Controller('buildings/:buildingUuid/floorplans')` with a `@Get()` list
// handler. The response is a wrapped-pagination envelope whose `paging.next` is a server-built
// path (from `request.path`), so continuation links resolve against the API origin.
const listFloorplansPath = (buildingUuid: string): string => `/resource-booking/buildings/${buildingUuid}/floorplans`;

// The endpoint caps `limit` at 100; asking for the max keeps the number of round trips down.
const FLOORPLANS_PAGE_SIZE = 100;

// CoworkersController, `@Controller('buildings/:buildingUuid/coworkers')` with a `@Get()` search
// handler. Consumer-facing (READ_BOOKINGS); answers 403 while the building does not reveal names.
const searchCoworkersPath = (buildingUuid: string): string => `/resource-booking/buildings/${buildingUuid}/coworkers`;
// BookingLimitsDaysController (resource-booking-service, RES-1134) is
// `@Controller('buildings/:buildingUuid/resources/:resourceUuid/booking-limits')` with a
// `@Get('days')` handler. Tenant-facing and advisory only: it answers what the caller already
// holds so the UI can offer a swap, but the authoritative refusal (if any) still comes from
// Helix's write-time check.
const getBookingLimitDaysPath = (buildingUuid: string, resourceUuid: string): string =>
  `/resource-booking/buildings/${buildingUuid}/resources/${resourceUuid}/booking-limits/days`;

const coworkerGroupsPath = (buildingUuid: string): string =>
  `/resource-booking/buildings/${buildingUuid}/coworker-groups`;

// TODO(RES-1002): swap this hand-rolled fetch for a generated `@hqo/resource-booking-client` call
// once the published package exposes `getBookingConversationsUnreadCounts` — the controller's
// swagger operationId already matches, but the client hasn't caught up yet (checked at 1.1.28).
// Keep this in sync with GetUnreadCountsQueryDto / GetBookingsUnreadCountsResponseDto until then.
//
// TODO(RES-1001): same gap for `getBookingMessagesConversation` — the source client.ts in the
// resource-booking-service repo already generates this function (matching operationId
// 'getBookingMessagesConversation'), but the installed `@hqo/resource-booking-client@1.1.28` dist in
// this repo's node_modules has zero occurrences of "conversation" anywhere in its compiled output —
// the published package genuinely lags the repo's checked-in generated source under the same
// version number. Swap to the real generated function once a republish catches up.
export class ResourceBookingServiceClient {
  constructor(private baseURL: string, private authToken: string | null) {}

  private get requestOptions() {
    return {
      baseURL: this.baseURL,
      headers: {
        ...(this.authToken ? { Authorization: this.authToken } : {}),
      },
    };
  }

  getResourceAvailableDates(
    resourceUuid: string,
    { from, to }: Pick<GetResourceAvailableDatesParams, 'from' | 'to'>,
  ): Observable<GetResourceAvailableDatesAPIResponse> {
    const startDate = from.replace(/\./g, '-');
    const endDate = to.replace(/\./g, '-');

    return rxFrom(getAvailableDates({ resourceId: resourceUuid, startDate, endDate }, this.requestOptions)).pipe(
      map(({ availableDates }) => ({
        data: { available_dates: availableDates, indexOfMonth: '' },
      })),
    );
  }

  getResourceTimeRanges(
    resourceUuid: string,
    params: GetResourceTimeRangesParams,
  ): Observable<GetResourceTimeRangesAPIResponse> {
    let startDate: string;
    let endDate: string;

    if ('startDates' in params) {
      const dates = params.startDates.split(',').map(date => date.trim());
      [startDate] = dates;
      endDate = dates[dates.length - 1];
    } else {
      startDate = params.start.substring(0, 10);
      endDate = params.end.substring(0, 10);
    }

    return rxFrom(getTimeRanges({ resourceId: resourceUuid, startDate, endDate }, this.requestOptions)).pipe(
      map(({ timeRanges, presetBookableWindows }) => ({
        data: {
          time_ranges: timeRanges.map(timeRange => ({
            date: timeRange.date,
            time: timeRange.time,
            available: timeRange.available,
            minimum_duration: timeRange.minimumDuration,
            maximum_duration: (timeRange.maximumDuration as unknown as number) ?? undefined,
            booking_window_id: timeRange.bookingWindowId as unknown as number,
            time_interval: timeRange.timeInterval ?? null,
            max_duration: (timeRange.maximumDuration as unknown as number) ?? null,
          })),
          preset_bookable_windows: presetBookableWindows?.map(presetBookableWindow => ({
            date: new Date(presetBookableWindow.date),
            time: presetBookableWindow.time,
            available: presetBookableWindow.available,
            duration: presetBookableWindow.duration,
            booking_window_id: presetBookableWindow.bookingWindowId as unknown as number,
            display_label: presetBookableWindow.displayLabel,
          })),
        },
      })),
    );
  }

  getBookingConversationsUnreadCounts(bookingIds: number[]): Observable<BookingConversationsUnreadCountsMap> {
    const query = new URLSearchParams();
    bookingIds.forEach(bookingId => query.append('bookingIds', String(bookingId)));

    const request = fetch(`${this.baseURL}${UNREAD_COUNTS_PATH}?${query.toString()}`, {
      headers: this.requestOptions.headers,
    }).then(async response => {
      const body = (await response.json()) as GetBookingConversationsUnreadCountsResponse;

      if (!response.ok) {
        throw body;
      }

      return body;
    });

    return rxFrom(request).pipe(map(({ data }) => data));
  }

  /*
   * RES-1336 — one entry per vendor whose add-ons were attached to this booking, carrying the work
   * order the charge hangs off.
   *
   * ⛔ A REFUSAL MUST NOT HANG THE WAIT. The read sits behind `CAN_MANAGE_TENANT_WORK_ORDERS`; a
   * tenant without that role at the building gets a 403, and the right answer is "no payment here"
   * rather than an error that strands them — the add-ons are ordered either way and the vendor can
   * still bill.
   *
   * ⚠️ So this deliberately does NOT single the 403 out, where the previous hand-rolled fetch did.
   * The generated client throws the parsed body rather than the Response, so there is no HTTP status
   * to branch on (the Nest error body happens to echo `statusCode`, but keying on an error body's
   * shape is a worse contract than the status it replaced). It does not need singling out: every
   * failure of this read is already caught by `useAddOnPaymentQuery` and answered as "nothing to
   * pay", so a thrown 403 and an empty list end in the same place.
   *
   * ⚠️ Narrower than it looks: the service also requires the caller to OWN the booking, so this
   * cannot be walked across other tenants' bookings even with the role.
   */
  getBookingAddOns(buildingUuid: string, bookingId: number): Observable<BookingAddOnsRead> {
    return rxFrom(getBookingAddOns({ buildingUuid, bookingId }, this.requestOptions)).pipe(
      /* `canOrderAddOns` is coerced rather than defaulted true: it is a required field, so an absent
       * one means a response this app does not understand, and the safe reading of that is "no". */
      map(
        (response): BookingAddOnsRead => ({
          vendors: response?.addOns ?? [],
          canOrderAddOns: Boolean(response?.canOrderAddOns),
          cannotOrderReason: response?.cannotOrderReason ?? null,
        }),
      ),
    );
  }

  /*
   * Stand every live vendor request on this booking down, keeping the room booked. Never rejects: the
   * service distinguishes cancelled, refused for good and refused for now, and a throw would flatten
   * all three. It cancels every vendor or none, so `vendorCompanyUuids` is the whole order or empty.
   * The stand-down is asynchronous — a `cancelled` outcome means the links are settled, not that
   * work-orders has finished, so the caller must re-read the add-ons endpoint for the payment state.
   * `fetch` is wrapped only to capture the status while still calling the generated function; see
   * `addOnCancelFailure` for why the error body's `statusCode` is not good enough.
   */
  cancelBookingAddOns(buildingUuid: string, bookingId: number): Observable<AddOnCancelOutcome> {
    /* Written by the wrapper below before the generated code can throw, so the catch can read it. */
    let status: number | null = null;

    const captureStatus: typeof fetch = async (input, init) => {
      const response = await fetch(input, init);
      status = response.status;

      return response;
    };

    return rxFrom(
      cancelBookingAddOns({ buildingUuid, bookingId }, { ...this.requestOptions, fetch: captureStatus })
        .then(addOnCancelled)
        .catch(() => ({ kind: addOnCancelFailure(status) } as AddOnCancelOutcome)),
    );
  }

  // Resolves (lazily provisioning, server-side) the conversations-service conversation UUID backing
  // a given booking's message thread. `uuid` comes back `null` if the caller may not access it (the
  // controller's guard already enforces READ_BOOKINGS before this ever runs, so in practice this
  // repo should only ever see a real uuid once a booking id it owns is passed in).
  getBookingMessagesConversation(buildingUuid: string, bookingId: number): Observable<string | null> {
    const request = fetch(`${this.baseURL}${getBookingMessagesConversationPath(buildingUuid, bookingId)}`, {
      headers: this.requestOptions.headers,
    }).then(async response => {
      const body = (await response.json()) as GetBookingMessagesConversationResponse;

      if (!response.ok) {
        throw body;
      }

      return body;
    });

    return rxFrom(request).pipe(map(({ data }) => data.uuid));
  }

  // Resources placed on a floorplan with their availability, for the employee-facing map.
  //
  // `date` is sent as YYYY-MM-DD. The endpoint currently computes availability at the current
  // server time and ignores the param; it is passed ahead of the planned BE update so the date
  // selection is wired end-to-end (same call shape resource-booking-admin already uses).
  getFloorplanResourcesAvailability(
    buildingUuid: string,
    floorplanUuid: string,
    date?: string,
  ): Observable<FloorplanResourceAvailabilityResponse[]> {
    const query = date ? `?${new URLSearchParams({ date }).toString()}` : '';
    const url = `${this.baseURL}${getFloorplanResourcesAvailabilityPath(buildingUuid, floorplanUuid)}${query}`;

    const request = fetch(url, { headers: this.requestOptions.headers }).then(async response => {
      const body = (await response.json()) as GetFloorplanResourcesAvailabilityResponse;

      if (!response.ok) {
        throw body;
      }

      return body;
    });

    return rxFrom(request).pipe(map(({ data }) => data));
  }

  // The requester's visible coworkers (their company in the building), matched by name, each with
  // that day's booking. The BE decides visibility and strips identity server-side; a 403 means the
  // building has coworker names switched off — the caller gates the UI on the config before asking.
  searchCoworkers(
    buildingUuid: string,
    { query, date, limit, offset }: SearchCoworkersParams,
  ): Observable<{ coworkers: CoworkerResponse[]; total: number }> {
    const search = new URLSearchParams();

    if (query) search.set('query', query);
    if (date) search.set('date', date);
    if (limit !== undefined) search.set('limit', String(limit));
    if (offset !== undefined) search.set('offset', String(offset));

    const queryString = search.toString();
    const url = `${this.baseURL}${searchCoworkersPath(buildingUuid)}${queryString ? `?${queryString}` : ''}`;

    const request = fetch(url, { headers: this.requestOptions.headers }).then(async response => {
      const body = (await response.json()) as SearchCoworkersResponse;

      if (!response.ok) {
        throw body;
      }

      return body;
    });

    return rxFrom(request).pipe(map(({ data }) => data));
  }

  // Days in `[startDate, endDate]` (inclusive, YYYY-MM-DD) the caller has already reached a
  // booking-limit cap covering this resource, with the blocking rule and the bookings held under
  // it. RES-1125 only ever asks for a single day (startDate === endDate) to gate the CTA, but the
  // endpoint itself speaks in ranges (BE caps the span at 62 days), so the shape is kept as-is
  // rather than narrowed to one day here.
  getBookingLimitDays(
    buildingUuid: string,
    resourceUuid: string,
    { startDate, endDate }: { startDate: string; endDate: string },
  ): Observable<BookingLimitDay[]> {
    const query = new URLSearchParams({ startDate, endDate }).toString();
    const url = `${this.baseURL}${getBookingLimitDaysPath(buildingUuid, resourceUuid)}?${query}`;

    const request = fetch(url, { headers: this.requestOptions.headers }).then(async response => {
      const body = (await response.json()) as BookingLimitDaysResponse;

      if (!response.ok) {
        throw body;
      }

      return body;
    });

    return rxFrom(request).pipe(map(({ data }) => data));
  }

  // The requester's own coworker groups ("teams", §C3) in the building. Personal by definition: the BE
  // only ever answers with groups the caller owns, and drops members who left the visibility scope.
  listCoworkerGroups(buildingUuid: string): Observable<CoworkerGroupResponse[]> {
    return rxFrom(
      this.requestJson<ListCoworkerGroupsResponse>(`${this.baseURL}${coworkerGroupsPath(buildingUuid)}`),
    ).pipe(map(({ data }) => data));
  }

  createCoworkerGroup(buildingUuid: string, body: CreateCoworkerGroupBody): Observable<CoworkerGroupResponse> {
    return rxFrom(
      this.requestJson<CoworkerGroupEnvelopeResponse>(`${this.baseURL}${coworkerGroupsPath(buildingUuid)}`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    ).pipe(map(({ data }) => data));
  }

  updateCoworkerGroup(
    buildingUuid: string,
    groupUuid: string,
    body: UpdateCoworkerGroupBody,
  ): Observable<CoworkerGroupResponse> {
    return rxFrom(
      this.requestJson<CoworkerGroupEnvelopeResponse>(
        `${this.baseURL}${coworkerGroupsPath(buildingUuid)}/${groupUuid}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    ).pipe(map(({ data }) => data));
  }

  deleteCoworkerGroup(buildingUuid: string, groupUuid: string): Observable<void> {
    return rxFrom(
      this.requestJson<void>(`${this.baseURL}${coworkerGroupsPath(buildingUuid)}/${groupUuid}`, { method: 'DELETE' }),
    );
  }

  // Monday–Friday attendance of the requester's coworkers ("Plan your week", and the focus-mode banner's
  // "N of M booked today"). Scope is resolved server-side: the whole visible directory, or one group.
  getCoworkerAttendance(
    buildingUuid: string,
    params: CoworkerAttendanceParams,
  ): Observable<CoworkerAttendanceResponse> {
    const search = new URLSearchParams({ weekStart: params.weekStart });

    if ('groupUuid' in params) {
      search.set('groupUuid', params.groupUuid);
    } else {
      search.set('scope', params.scope);
    }

    return rxFrom(
      this.requestJson<CoworkerAttendanceEnvelopeResponse>(
        `${this.baseURL}${searchCoworkersPath(buildingUuid)}/attendance?${search.toString()}`,
      ),
    ).pipe(map(({ data }) => data));
  }

  // JSON request against resource-booking-service with the caller's auth. A non-2xx answer rejects
  // with the parsed error body (the BE's `{ statusCode, message }`), a 204 resolves with nothing.
  private async requestJson<T>(url: string, init: RequestInit = {}): Promise<T> {
    const response = await fetch(url, {
      ...init,
      headers: {
        ...this.requestOptions.headers,
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      },
    });

    if (response.status === 204) {
      return undefined as T;
    }

    const body = (await response.json()) as T;

    if (!response.ok) {
      throw body;
    }

    return body;
  }

  // Every floorplan of a building, ordered by `sortOrder` ascending (BE guarantees the order).
  //
  // Walks the server's `paging.next` links rather than recomputing offsets, so a building with more
  // floorplans than one page never silently loses its tail.
  getFloorplans(buildingUuid: string): Observable<FloorplanResponse[]> {
    return rxFrom(this.fetchAllFloorplans(buildingUuid));
  }

  private async fetchAllFloorplans(buildingUuid: string): Promise<FloorplanResponse[]> {
    const floorplans: FloorplanResponse[] = [];
    // A server that repeats a link (or returns a cycle) must not drive an unbounded request loop.
    const visitedUrls = new Set<string>();
    let url: string | null = `${this.baseURL}${listFloorplansPath(
      buildingUuid,
    )}?offset=0&limit=${FLOORPLANS_PAGE_SIZE}`;

    /* eslint-disable no-await-in-loop -- pages must be fetched sequentially */
    while (url) {
      if (visitedUrls.has(url)) {
        throw new Error('Floorplan pagination cycle detected');
      }
      visitedUrls.add(url);

      const response = await fetch(url, { headers: this.requestOptions.headers });
      const body = (await response.json()) as ListFloorplansResponse;

      if (!response.ok) {
        throw body;
      }

      floorplans.push(...(body.data?.floorplans ?? []));
      url = this.resolveNextPageUrl(body.paging?.next);
    }
    /* eslint-enable no-await-in-loop */

    return floorplans;
  }

  // Resolves `paging.next` against the configured API base. Returns null only for an explicit null
  // (the last page); a malformed link, or one that escapes the base, is a broken response and
  // throws, so the caller retries instead of quietly acting on a partial list.
  //
  // The base is checked by path as well as origin: when `baseURL` carries a path prefix (e.g.
  // `https://host/api`), a root-relative `next` resolves at the origin root and would leave the
  // prefix the first request was made under while still passing an origin-only check.
  private resolveNextPageUrl(next: string | null | undefined): string | null {
    if (next === null || next === undefined) {
      return null;
    }

    if (typeof next !== 'string' || next.trim().length === 0) {
      throw new Error('Invalid floorplan pagination link');
    }

    // A trailing slash makes the base a directory, so a relative `next` resolves under it rather
    // than replacing its last segment, and makes `pathname` a prefix we can compare against.
    const base = new URL(this.baseURL.endsWith('/') ? this.baseURL : `${this.baseURL}/`);
    let resolved: URL;

    try {
      resolved = new URL(next, base);
    } catch {
      throw new Error('Invalid floorplan pagination link');
    }

    if (resolved.origin !== base.origin) {
      throw new Error('Floorplan pagination link has an invalid origin');
    }

    if (!resolved.pathname.startsWith(base.pathname)) {
      throw new Error('Floorplan pagination link is outside the API path');
    }

    return resolved.toString();
  }
}
